/**
 * The runtime configuration document `FE-06` and `FE-07` require: the API origin, environment
 * name, and telemetry endpoint this build resolves at boot, fetched from the application's own
 * origin rather than baked in at build time.
 *
 * The POC's
 * https://github.com/Merchant-Trends/dce/blob/dce-final/archive/poc/apps/web/src/api.ts#L4
 * reads `VITE_API_BASE_URL` from `import.meta.env` instead, which binds one built artifact to
 * one API origin. This module is the replacement: the same `dist/` digest promotes across every
 * environment and AWS account unchanged, because the origin it talks to is resolved after the
 * bundle loads, from a document the bundle never embeds.
 *
 * `tools/nexus-plugin/templates/web/src/runtime-config.ts` is a deliberate copy of this
 * module, so that a generated application fails closed exactly as this one does.
 * `scripts/templates/web-runtime-config-parity.spec.mjs` fails when they drift: change both in
 * the same commit.
 */

/**
 * The Nexus apps a frame can link to. Each is deployed on its own, so a link is the origin the
 * runtime document names for it; an app the document does not name is shown but cannot be opened.
 */
export const NEXUS_APPS = ["checkout", "portal"] as const;

export type NexusApp = (typeof NEXUS_APPS)[number];

export type RuntimeLinks = Readonly<Partial<Record<NexusApp, string>>>;

export interface RuntimeConfig {
  readonly apiOrigin: string;
  readonly environment: string;
  readonly links: RuntimeLinks;
  readonly telemetryEndpoint: string;
}

const REQUIRED_STRING_FIELDS = ["apiOrigin", "environment", "telemetryEndpoint"] as const;

const RUNTIME_CONFIG_PATH = "/runtime-config.json";

export class RuntimeConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RuntimeConfigError";
  }
}

// Both fields are network endpoints this build will `fetch()` or send telemetry to, never a
// place to open an arbitrary URL scheme - `new URL()` alone happily parses `javascript:...`,
// `data:...`, or a browser-extension scheme, none of which are a usable HTTP(S) endpoint, and
// accepting one here would just move the failure from a legible config-time refusal to an
// obscure runtime one.
const HTTP_SCHEMES = new Set(["http:", "https:"]);

function isHttpUrl(value: string): boolean {
  try {
    return HTTP_SCHEMES.has(new URL(value).protocol);
  } catch {
    return false;
  }
}

/**
 * An origin is a URL's scheme, host, and port and nothing else, in the exact form `new URL`
 * would normalise it to - round-tripping through it is what catches every way a value can fail
 * to be one: a path, query, or fragment; a trailing slash; or a port stated explicitly when it is
 * already the scheme's default (`new URL("https://api.example.com:443").origin` drops `:443`, so
 * a value that states it does not match its own normalised form).
 */
function isOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    return HTTP_SCHEMES.has(url.protocol) && url.origin === value;
  } catch {
    return false;
  }
}

/**
 * Validates an already-parsed JSON value against the runtime configuration's shape. Every
 * violation is collected before throwing, so a reader fixing a malformed document sees every
 * problem in the file at once rather than one refusal per re-fetch.
 */
export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    // `typeof null === "object"` in JavaScript, so without this branch a null document would be
    // reported as "got object" - true of the type, not of the value, and useless for diagnosing
    // an actually-null runtime-config.json.
    const kind = raw === null ? "null" : Array.isArray(raw) ? "an array" : typeof raw;
    throw new RuntimeConfigError(`${RUNTIME_CONFIG_PATH} must be a JSON object, got ${kind}`);
  }

  const record = raw as Record<string, unknown>;
  const problems: string[] = [];

  for (const field of REQUIRED_STRING_FIELDS) {
    const value = record[field];
    if (typeof value !== "string" || value.trim() === "") {
      problems.push(`"${field}" must be a non-empty string`);
    }
  }

  // Format checks run against whichever fields are actually present as non-empty strings, even
  // when a sibling field failed the loop above - a document broken two ways has to report both
  // breaks in the same refusal, not one per re-fetch, which is exactly what throwing here on the
  // presence check alone used to prevent. Each check is guarded on its own field's presence
  // rather than on `problems` being empty: checking a field the loop already flagged would either
  // validate `undefined` or an empty string, producing a second, redundant complaint about the
  // same field instead of a new one about a different field.
  const apiOrigin = record.apiOrigin;
  if (typeof apiOrigin === "string" && apiOrigin.trim() !== "" && !isOrigin(apiOrigin)) {
    problems.push(
      `"apiOrigin" must be an http:// or https:// origin in its canonical form - scheme, host, ` +
        `and port only, no path, query, fragment, trailing slash, or redundant default port - ` +
        `got ${JSON.stringify(apiOrigin)}`,
    );
  }
  const telemetryEndpoint = record.telemetryEndpoint;
  if (
    typeof telemetryEndpoint === "string" &&
    telemetryEndpoint.trim() !== "" &&
    !isHttpUrl(telemetryEndpoint)
  ) {
    problems.push(
      `"telemetryEndpoint" must be an absolute http:// or https:// URL, got ${JSON.stringify(telemetryEndpoint)}`,
    );
  }

  const links = parseLinks(record.links, problems);

  if (problems.length > 0) {
    throw new RuntimeConfigError(`invalid ${RUNTIME_CONFIG_PATH}: ${problems.join("; ")}`);
  }

  return {
    apiOrigin: apiOrigin as string,
    environment: (record.environment as string).trim(),
    links,
    telemetryEndpoint: telemetryEndpoint as string,
  };
}

const isNexusApp = (name: string): name is NexusApp =>
  (NEXUS_APPS as readonly string[]).includes(name);

/**
 * `links` is optional: a document that names no other app links to none. When present, every name
 * must be a known app and every value an origin, checked as `apiOrigin` is. An unknown name is
 * refused rather than ignored, because a misspelt `"portl"` would otherwise leave its link
 * disabled with nothing to say why.
 */
function parseLinks(raw: unknown, problems: string[]): RuntimeLinks {
  if (raw === undefined) {
    return {};
  }
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    problems.push(`"links" must be an object naming each app's origin, got ${JSON.stringify(raw)}`);
    return {};
  }
  const links: Partial<Record<NexusApp, string>> = {};
  for (const [name, value] of Object.entries(raw)) {
    if (!isNexusApp(name)) {
      problems.push(
        `"links" names an unknown app ${JSON.stringify(name)}; the known apps are ${NEXUS_APPS.join(", ")}`,
      );
    } else if (typeof value !== "string" || !isOrigin(value)) {
      problems.push(
        `"links.${name}" must be an http:// or https:// origin in its canonical form, got ` +
          JSON.stringify(value),
      );
    } else {
      links[name] = value;
    }
  }
  return links;
}

/**
 * Fetches and validates the runtime configuration document. `fetchImpl` is a parameter rather than
 * a read of the global, per `docs/TESTING-STANDARDS.md` "Seams": the same reason `mount-point.ts`
 * takes `document` as a parameter, so a test can supply a fake response without a real network call
 * or a global mock.
 *
 * There is no fallback value anywhere in this function. `FE-07` requires a missing or malformed
 * document to fail closed, never to default silently to `localhost` or any other compiled origin -
 * every failure path below throws a `RuntimeConfigError` naming what went wrong instead.
 */
export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  let response: Response;
  try {
    response = await fetchImpl(RUNTIME_CONFIG_PATH);
  } catch (cause) {
    throw new RuntimeConfigError(
      `could not reach ${RUNTIME_CONFIG_PATH}: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  if (!response.ok) {
    throw new RuntimeConfigError(
      `${RUNTIME_CONFIG_PATH} responded ${String(response.status)} ${response.statusText}`,
    );
  }

  // A static host with SPA-style fallback routing (`vite preview` included) serves `index.html`
  // with a 200 for an unmatched path rather than a 404, so a genuinely missing runtime document
  // never reaches the status check above - it reaches here as HTML. That would still fail closed
  // on the JSON parse below, but as an unhelpful syntax error; naming the wrong content type is
  // the actionable version of the same refusal.
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("json")) {
    throw new RuntimeConfigError(
      `${RUNTIME_CONFIG_PATH} responded with content-type ${JSON.stringify(contentType)}, not JSON - ` +
        "check that the runtime document is actually being served at this path rather than falling " +
        "back to the application shell",
    );
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch (cause) {
    throw new RuntimeConfigError(
      `${RUNTIME_CONFIG_PATH} is not valid JSON: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  return parseRuntimeConfig(raw);
}
